/**
 * ActivityTab — full event timeline.
 *
 * Vertical list, newest first. Filterable by event type. Each entry
 * has a colored dot keyed to the event-class so the eye can scan.
 */
import React, { useState, useMemo } from 'react';
import { useManifest } from '../ManifestContext';
import { getOrderMocks, TYPE_LABEL } from '../mocks';
import {
  Activity, MessageSquare, FileText, FileSignature, DollarSign,
  Camera, Truck, Sparkles, Send, CheckCircle2, Mail,
} from 'lucide-react';

const TYPE_ICON = {
  'step.completed':   <CheckCircle2 size={14} />,
  'message.sent':     <Send size={14} />,
  'message.received': <Mail size={14} />,
  'doc.generated':    <FileText size={14} />,
  'doc.signed':       <FileSignature size={14} />,
  'quote.received':   <DollarSign size={14} />,
  'quote.picked':     <CheckCircle2 size={14} />,
  'payment.sent':     <DollarSign size={14} />,
  'payment.received': <DollarSign size={14} />,
  'photo.received':   <Camera size={14} />,
  'tracking.update':  <Truck size={14} />,
  'copilot.action':   <Sparkles size={14} />,
};

const formatTs = (iso) => new Date(iso).toLocaleString('en-CA', {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
});

const ActivityTab = () => {
  const { activeOrder } = useManifest();
  const mocks = getOrderMocks(activeOrder.id);
  const [filter, setFilter] = useState('all');

  const events = useMemo(() => {
    const sorted = [...mocks.activity].sort((a, b) => new Date(b.ts) - new Date(a.ts));
    if (filter === 'all') return sorted;
    if (filter === 'communication') {
      return sorted.filter((e) => e.type.startsWith('message.') || e.type === 'photo.received');
    }
    if (filter === 'documents') {
      return sorted.filter((e) => e.type.startsWith('doc.'));
    }
    if (filter === 'copilot') {
      return sorted.filter((e) => e.type === 'copilot.action');
    }
    return sorted;
  }, [mocks.activity, filter]);

  const filterChip = (id, label) => (
    <button
      type="button"
      key={id}
      className={`activity-filter-chip ${filter === id ? 'active' : ''}`}
      onClick={() => setFilter(id)}
    >
      {label}
    </button>
  );

  return (
    <div className="manifest-tab-pane">
      <div className="manifest-section-head">
        <h3><Activity size={16} /> Activity timeline</h3>
        <span>{events.length} events</span>
      </div>

      <div className="activity-filter-row">
        {filterChip('all', 'All events')}
        {filterChip('communication', <><MessageSquare size={11} /> Communication</>)}
        {filterChip('documents', <><FileText size={11} /> Documents</>)}
        {filterChip('copilot', <><Sparkles size={11} /> Co-Pilot actions</>)}
      </div>

      <ul className="activity-tab-list">
        {events.map((e) => {
          const cls = e.type.split('.')[0];
          return (
            <li key={e.id} className={`activity-tab-item type-${cls}`}>
              <div className={`activity-tab-icon type-${cls}`}>
                {TYPE_ICON[e.type] || <Activity size={14} />}
              </div>
              <div className="activity-tab-main">
                <div className="activity-tab-summary">{e.summary}</div>
                <div className="activity-tab-meta">
                  <strong>{e.actor}</strong> · {TYPE_LABEL[e.type] || e.type} · {formatTs(e.ts)}
                </div>
                {e.meta?.fileName && (
                  <div className="activity-tab-doc"><FileText size={11} /> {e.meta.fileName}</div>
                )}
              </div>
            </li>
          );
        })}
        {events.length === 0 && (
          <li className="manifest-empty manifest-empty-inline">
            <p>No events match this filter.</p>
          </li>
        )}
      </ul>
    </div>
  );
};

export default ActivityTab;
